import React, { useState, useEffect, useMemo, useReducer, useRef, useCallback } from "react";
import { createClient } from "@supabase/supabase-js";
import bcrypt from "bcryptjs";

// ─── SUPABASE CONFIG ────────────────────────────────────────────────────────
// Vervang deze waarden met jouw eigen Supabase project URL en anon key.
// Je vindt ze in je Supabase project onder Settings > API.
const SUPABASE_URL = "https://muuizwkowetqgntpygct.supabase.co";
const SUPABASE_ANON_KEY = "sb_publishable_wLEXfQ46iUGRqpBbVL9Rfg_R8Dku7qW";

const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

// ─── CONSTANTEN ─────────────────────────────────────────────────────────────
const ROOM_ID = "f1-2026";
const WHATSAPP_NUMBER = "31681811410";

const drivers = [
  "Max Verstappen","Isack Hadjar","George Russell","Andrea Kimi Antonelli",
  "Charles Leclerc","Lewis Hamilton","Lando Norris","Oscar Piastri",
  "Pierre Gasly","Franco Colapinto","Liam Lawson","Arvid Lindblad",
  "Fernando Alonso","Lance Stroll","Alexander Albon","Carlos Sainz",
  "Esteban Ocon","Oliver Bearman","Nico Hülkenberg","Gabriel Bortoleto",
  "Sergio Pérez","Valtteri Bottas"
];

const raceSchedule = [
  { name: "Selecteer race", start: null },
  { name: "Australian Grand Prix", start: "2026-02-07T06:00:00Z" },
  { name: "Chinese Sprint race", start: "2026-02-13T08:30:00Z" },
  { name: "Chinese Grand Prix", start: "2026-03-14T08:00:00Z" },
  { name: "Japanese Grand Prix", start: "2026-03-28T06:00:00Z" },
  { name: "Bahrain Canceled", start: "2026-02-11T18:00:00Z" },
  { name: "Saudi Arabian Canceled", start: "2026-02-18T19:00:00Z" },
  { name: "Miami Sprint race", start: "2026-05-01T20:30:00Z" },
  { name: "Miami Grand Prix", start: "2026-05-02T20:00:00Z" },
  { name: "Canadian Sprint. Race", start: "2026-05-22T20:30:00Z" },
  { name: "Canadian Grand Prix", start: "2026-05-23T20:00:00Z" },
  { name: "Monaco Grand Prix", start: "2026-06-06T14:00:00Z" },
  { name: "Spanish Grand Prix (Barcelona)", start: "2026-06-13T14:00:00Z" },
  { name: "Austrian Grand Prix", start: "2026-06-27T14:00:00Z" },
  { name: "British Sprint race", start: "2026-07-03T15:30:00Z" },
  { name: "British Grand Prix", start: "2026-07-04T15:00:00Z" },
  { name: "Belgian Grand Prix", start: "2026-07-18T14:00:00Z" },
  { name: "Hungarian Grand Prix", start: "2026-07-25T14:00:00Z" },
  { name: "Dutch Sprint race", start: "2026-08-21T14:30:00Z" },
  { name: "Dutch Grand Prix", start: "2026-08-22T14:00:00Z" },
  { name: "Italian Grand Prix", start: "2026-09-05T14:00:00Z" },
  { name: "Spanish Grand Prix (Madrid)", start: "2026-09-12T14:00:00Z" },
  { name: "Azerbaijan Grand Prix", start: "2026-09-26T14:00:00Z" },
  { name: "Singapore Sprint race", start: "2026-10-09T12:30:00Z" },
  { name: "Singapore Grand Prix", start: "2026-10-10T13:00:00Z" },
  { name: "United States Grand Prix (Austin)", start: "2026-10-24T15:00:00Z" },
  { name: "Mexico City Grand Prix", start: "2026-10-31T14:00:00Z" },
  { name: "São Paulo Grand Prix", start: "2026-11-07T18:00:00Z" },
  { name: "Las Vegas Grand Prix", start: "2026-11-21T04:00:00Z" },
  { name: "Qatar Grand Prix", start: "2026-11-28T18:00:00Z" },
  { name: "Abu Dhabi Grand Prix", start: "2026-12-05T14:00:00Z" }
];

const points = { first: 25, second: 18, third: 15, latest: 11, pole: 10 };

// ─── SCORE BEREKENING ────────────────────────────────────────────────────────
export function calculateScore(predictions, results) {
  let total = 0;
  Object.keys(results || {}).forEach(r => {
    const p = predictions && predictions[r];
    const res = results[r];
    if (!p || !res) return;
    Object.keys(points).forEach(k => { if (p[k] && res[k] && p[k] === res[k]) total += points[k]; });
  });
  return total;
}

// ─── PREDICTIONS REDUCER ─────────────────────────────────────────────────────
function predictionsReducer(state, action) {
  switch (action.type) {
    case 'update': {
      const { player, race, key, value } = action.payload;
      if (!state[player]) state[player] = {};
      if (race && key) {
        return {
          ...state,
          [player]: {
            ...state[player],
            [race]: { ...(state[player][race] || {}), [key]: value }
          }
        };
      } else if (value) {
        if (!value || Object.keys(value).length === 0) {
          const newState = { ...state };
          delete newState[player];
          return newState;
        }
        return { ...state, [player]: value };
      }
      return state;
    }
    case 'deletePlayer': {
      const newState = { ...state };
      delete newState[action.payload];
      return newState;
    }
    case 'deletePlayerRace': {
      const { player, race } = action.payload;
      const newState = { ...state };
      if (newState[player]) {
        const playerCopy = { ...newState[player] };
        delete playerCopy[race];
        if (Object.keys(playerCopy).length === 0) {
          delete newState[player];
        } else {
          newState[player] = playerCopy;
        }
      }
      return newState;
    }
    case 'clearRace': {
      const newState = { ...state };
      Object.keys(newState).forEach(p => {
        if (newState[p]) {
          delete newState[p][action.payload];
          if (Object.keys(newState[p]).length === 0) delete newState[p];
        }
      });
      return newState;
    }
    case 'sync': {
      const newPredictions = action.payload || {};
      const cleanedState = {};
      Object.keys(newPredictions).forEach(player => {
        const playerData = newPredictions[player];
        if (playerData && Object.keys(playerData).length > 0) {
          cleanedState[player] = playerData;
        }
      });
      return cleanedState;
    }
    default: return state;
  }
}

// ─── SUPABASE DATA LAAG ──────────────────────────────────────────────────────
// Alle Firebase-calls zijn hier vervangen door Supabase equivalenten.
//
// Database structuur (zie ook SUPABASE_SETUP.sql):
//   users         → id, name, password, is_admin, created_at
//   rooms         → id (text = ROOM_ID), predictions (jsonb), results (jsonb)
//
// Opmerking: wachtwoorden worden als plaintext opgeslagen, net als in de
// originele Firebase versie. Voor productie wordt bcrypt aanbevolen.

let cachedUsersData = null; // module-level cache, zelfde als origineel

async function sbGetUsers() {
  if (cachedUsersData) return cachedUsersData;
  const { data, error } = await supabase.from("users").select("*");
  if (error) throw error;
  const map = {};
  (data || []).forEach(u => {
    map[u.name] = { password: u.password, isAdmin: u.is_admin, createdAt: u.created_at };
  });
  cachedUsersData = map;
  return map;
}

async function sbSetFirstAdmin(name, password) {
  const hashedPassword = await bcrypt.hash(password, 10);
  const { error } = await supabase.from("users").insert({
    name,
    password: hashedPassword,
    is_admin: true,
    created_at: new Date().toISOString()
  });
  if (error) throw error;
  cachedUsersData = {
    [name]: { password: hashedPassword, isAdmin: true, createdAt: new Date().toISOString() }
  };
}

async function sbCreateUser(name, password, isAdmin = false) {
  const hashedPassword = await bcrypt.hash(password, 10);
  const { error } = await supabase.from("users").insert({
    name,
    password: hashedPassword,
    is_admin: isAdmin,
    created_at: new Date().toISOString()
  });
  if (error) throw error;
  if (cachedUsersData) {
    cachedUsersData = {
      ...cachedUsersData,
      [name]: { password: hashedPassword, isAdmin, createdAt: new Date().toISOString() }
    };
  }
}

async function sbUpdateUserPassword(name, newPassword) {
  const hashedPassword = await bcrypt.hash(newPassword, 10);
  const { error } = await supabase
    .from("users")
    .update({ password: hashedPassword })
    .eq("name", name);
  if (error) throw error;
  if (cachedUsersData && cachedUsersData[name]) {
    cachedUsersData = {
      ...cachedUsersData,
      [name]: { ...cachedUsersData[name], password: hashedPassword }
    };
  }
}

async function sbToggleAdmin(name, newStatus) {
  const { error } = await supabase
    .from("users")
    .update({ is_admin: newStatus })
    .eq("name", name);
  if (error) throw error;
  if (cachedUsersData && cachedUsersData[name]) {
    cachedUsersData = {
      ...cachedUsersData,
      [name]: { ...cachedUsersData[name], isAdmin: newStatus }
    };
  }
}

async function sbDeleteUser(name) {
  const { error } = await supabase.from("users").delete().eq("name", name);
  if (error) throw error;
  if (cachedUsersData) {
    const copy = { ...cachedUsersData };
    delete copy[name];
    cachedUsersData = copy;
  }
}

async function sbGetRoomData() {
  const { data, error } = await supabase
    .from("rooms")
    .select("predictions, results")
    .eq("id", ROOM_ID)
    .single();
  if (error && error.code !== "PGRST116") throw error; // PGRST116 = not found
  return data || { predictions: {}, results: {} };
}

async function sbUpdatePrediction(player, race, key, value) {
  // Lees huidige data, merge, en schrijf terug (JSONB deep merge via rpc of client-side)
  const current = await sbGetRoomData();
  const predictions = current.predictions || {};
  if (!predictions[player]) predictions[player] = {};
  if (!predictions[player][race]) predictions[player][race] = {};
  predictions[player][race][key] = value;
  await sbUpsertRoom(predictions, current.results || {});
}

async function sbUpsertRoom(predictions, results) {
  const { error } = await supabase.from("rooms").upsert({
    id: ROOM_ID,
    predictions,
    results
  });
  if (error) throw error;
}

async function sbDeletePlayerPredictions(player) {
  const current = await sbGetRoomData();
  const predictions = { ...(current.predictions || {}) };
  delete predictions[player];
  await sbUpsertRoom(predictions, current.results || {});
}

async function sbDeletePlayerRacePredictions(player, race) {
  const current = await sbGetRoomData();
  const predictions = { ...(current.predictions || {}) };
  if (predictions[player]) {
    const playerCopy = { ...predictions[player] };
    delete playerCopy[race];
    if (Object.keys(playerCopy).length === 0) {
      delete predictions[player];
    } else {
      predictions[player] = playerCopy;
    }
  }
  await sbUpsertRoom(predictions, current.results || {});
}

async function sbClearRace(race, currentPredictions) {
  const predictions = { ...currentPredictions };
  Object.keys(predictions).forEach(p => {
    if (predictions[p]) {
      delete predictions[p][race];
      if (Object.keys(predictions[p]).length === 0) delete predictions[p];
    }
  });
  const current = await sbGetRoomData();
  const results = { ...(current.results || {}) };
  delete results[race];
  await sbUpsertRoom(predictions, results);
  return { predictions, results };
}

// ─── UI COMPONENTEN ───────────────────────────────────────────────────────────
function StyledSelect({ label, value, options, onChange, disabled }) {
  return (
    <div style={{ marginBottom: '0.5rem', width: '100%' }}>
      <label style={{ display: 'block', marginBottom: '0.2rem', color: 'white', fontWeight: 'bold' }}>{label}</label>
      <select
        value={value || ""}
        onChange={e => onChange(e.target.value)}
        disabled={disabled}
        style={{
          width: '100%',
          padding: '0.7rem',
          fontSize: '1rem',
          borderRadius: '0.5rem',
          background: '#1f2937',
          color: 'white',
          border: '1px solid #fbbf24',
          appearance: 'none',
          opacity: disabled ? 0.5 : 1
        }}
      >
        <option value="" disabled hidden>-- Selecteer --</option>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}

function LockMessage({ raceName }) {
  const whatsappLink = `https://wa.me/${WHATSAPP_NUMBER}?text=Hallo,%20ik%20heb%20een%20vraag%20over%20de%20F1%20Pool%20voor%20${encodeURIComponent(raceName)}`;
  return (
    <div style={{ background: 'linear-gradient(135deg,#dc2626 0%,#991b1b 100%)', border: '2px solid #fbbf24', borderRadius: '1rem', padding: '1.5rem', marginBottom: '1.5rem', textAlign: 'center', boxShadow: '0 4px 6px rgba(0,0,0,0.3)' }}>
      <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🔒</div>
      <h3 style={{ color: '#fbbf24', margin: '0 0 0.5rem 0', fontSize: '1.25rem' }}>Deze race is gesloten!</h3>
      <p style={{ color: 'white', margin: '0 0 1rem 0', fontSize: '0.95rem' }}>
        De Qualifying voor <strong>{raceName}</strong> is al begonnen of geweest.<br />Voorspellingen zijn niet meer mogelijk.
      </p>
      <p style={{ color: '#fecaca', margin: '0 0 1rem 0', fontSize: '0.9rem' }}>Vragen of onduidelijkheden? Neem contact op:</p>
      <a href={whatsappLink} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', background: '#25D366', color: 'white', padding: '0.75rem 1.5rem', borderRadius: '2rem', textDecoration: 'none', fontWeight: 'bold', fontSize: '1rem' }}>
        <span>💬</span>WhatsApp ons
      </a>
    </div>
  );
}

function AlreadySubmittedMessage({ raceName, onClose }) {
  const whatsappLink = `https://wa.me/${WHATSAPP_NUMBER}?text=Hallo,%20ik%20wil%20mijn%20voorspellingen%20voor%20${encodeURIComponent(raceName)}%20wijzigen`;
  return (
    <div style={{ background: 'linear-gradient(135deg,#1e40af 0%,#1e3a8a 100%)', border: '2px solid #fbbf24', borderRadius: '1rem', padding: '1.5rem', marginBottom: '1.5rem', textAlign: 'center', boxShadow: '0 4px 6px rgba(0,0,0,0.3)' }}>
      <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📝</div>
      <h3 style={{ color: '#fbbf24', margin: '0 0 0.5rem 0', fontSize: '1.25rem' }}>Je hebt al voorspellingen ingevoerd!</h3>
      <p style={{ color: 'white', margin: '0 0 1rem 0', fontSize: '0.95rem' }}>
        Je hebt al voorspellingen gedaan voor <strong>{raceName}</strong>.<br />Wijzigingen zijn niet meer mogelijk.
      </p>
      <button onClick={onClose} style={{ padding: '0.5rem 1.5rem', borderRadius: '0.5rem', background: '#fbbf24', color: '#1e3a8a', fontWeight: 'bold', fontSize: '1rem', border: 'none', cursor: 'pointer', marginBottom: '1rem' }}>✅ OK</button>
      <p style={{ color: '#bfdbfe', margin: '0 0 1rem 0', fontSize: '0.9rem' }}>Foutje gemaakt? Neem contact op:</p>
      <a href={whatsappLink} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', background: '#25D366', color: 'white', padding: '0.75rem 1.5rem', borderRadius: '2rem', textDecoration: 'none', fontWeight: 'bold', fontSize: '1rem' }}>
        <span>💬</span>WhatsApp admin
      </a>
    </div>
  );
}

// ─── SETUP SCREEN ─────────────────────────────────────────────────────────────
function SetupScreen({ onSetupComplete }) {
  const [setupName, setSetupName] = useState('');
  const [setupPassword, setSetupPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const handleSetup = async (e) => {
    e.preventDefault();
    setError('');
    if (!setupName.trim() || !setupPassword) { setError('Vul alle velden in'); return; }
    if (setupPassword !== confirmPassword) { setError('Wachtwoorden komen niet overeen'); return; }
    if (setupPassword.length < 4) { setError('Wachtwoord moet minimaal 4 tekens bevatten'); return; }
    setIsCreating(true);
    try {
      await sbSetFirstAdmin(setupName.trim(), setupPassword);
      onSetupComplete(setupName.trim(), setupPassword);
    } catch (err) {
      console.error('Setup error:', err);
      setError('Er ging iets mis. Probeer opnieuw.');
      setIsCreating(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(to bottom right,#b91c1c,#000,#1f2937)', display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '1rem' }}>
      <div style={{ background: '#1f2937', borderRadius: '2rem', padding: '2rem', width: '100%', maxWidth: '400px', textAlign: 'center', border: '3px solid #dc2626', boxShadow: '0 10px 25px rgba(0,0,0,0.5)' }}>
        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🚀</div>
        <h1 style={{ color: '#facc15', fontSize: '1.8rem', marginBottom: '0.5rem' }}>Welkom bij F1 Pool!</h1>
        <p style={{ color: '#9ca3af', marginBottom: '2rem', fontSize: '0.95rem' }}>Er zijn nog geen gebruikers. Maak het eerste admin account aan om te beginnen.</p>
        {error && <div style={{ background: '#dc2626', color: 'white', padding: '0.75rem', borderRadius: '0.5rem', marginBottom: '1rem', fontSize: '0.9rem' }}>{error}</div>}
        <form onSubmit={handleSetup}>
          {[['text', 'Admin naam', setupName, setSetupName], ['password', 'Wachtwoord', setupPassword, setSetupPassword], ['password', 'Bevestig wachtwoord', confirmPassword, setConfirmPassword]].map(([type, ph, val, setter]) => (
            <div key={ph} style={{ marginBottom: '1rem' }}>
              <input type={type} placeholder={ph} value={val} onChange={e => setter(e.target.value)} disabled={isCreating}
                style={{ width: '100%', padding: '0.75rem', fontSize: '1rem', borderRadius: '0.5rem', background: '#111', color: 'white', border: '2px solid #fbbf24', boxSizing: 'border-box' }} required />
            </div>
          ))}
          <button type="submit" disabled={isCreating} style={{ width: '100%', padding: '0.75rem', fontSize: '1rem', borderRadius: '0.5rem', background: isCreating ? '#6b7280' : '#fbbf24', color: '#1f2937', fontWeight: 'bold', border: 'none', cursor: isCreating ? 'not-allowed' : 'pointer', marginBottom: '1rem' }}>
            {isCreating ? '⏳ Bezig met aanmaken...' : '✅ Eerste Admin Aanmaken'}
          </button>
        </form>
        <div style={{ marginTop: '1.5rem', padding: '1rem', background: 'rgba(251,191,36,0.1)', borderRadius: '0.5rem', border: '1px solid #fbbf24' }}>
          <p style={{ color: '#fbbf24', fontSize: '0.85rem', margin: 0 }}>💡 <strong>Tip:</strong> Onthoud deze gegevens goed! Je kunt later meer admins toevoegen vanuit het admin panel.</p>
        </div>
      </div>
    </div>
  );
}

// ─── AUTH SCREEN ──────────────────────────────────────────────────────────────
function AuthScreen({ onLogin, onRegister, error, successMessage }) {
  const [mode, setMode] = useState('login');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [localError, setLocalError] = useState('');
  const [showAdminLogin, setShowAdminLogin] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setLocalError('');
    if (mode === 'login') {
      onLogin(name.trim(), password);
    } else {
      if (password !== confirmPassword) { setLocalError('Wachtwoorden komen niet overeen!'); return; }
      if (password.length < 4) { setLocalError('Wachtwoord moet minimaal 4 tekens bevatten!'); return; }
      onRegister(name.trim(), password);
    }
  };

  const handleAdminLogin = (e) => {
    e.preventDefault();
    setLocalError('');
    onLogin(name.trim(), password, true);
  };

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(to bottom right,#b91c1c,#000,#1f2937)', display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '1rem' }}>
      <div style={{ background: '#1f2937', borderRadius: '2rem', padding: '2rem', width: '100%', maxWidth: '400px', textAlign: 'center', border: '3px solid #dc2626', boxShadow: '0 10px 25px rgba(0,0,0,0.5)' }}>
        <h1 style={{ color: '#facc15', fontSize: '2rem', marginBottom: '0.5rem' }}>🏁 F1 Pool 2026</h1>
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', background: '#111', padding: '0.3rem', borderRadius: '0.5rem' }}>
          <button onClick={() => { setShowAdminLogin(false); setLocalError(''); }} style={{ flex: 1, padding: '0.5rem', borderRadius: '0.3rem', border: 'none', background: !showAdminLogin ? '#fbbf24' : 'transparent', color: !showAdminLogin ? '#1f2937' : '#9ca3af', fontWeight: 'bold', cursor: 'pointer', fontSize: '0.9rem' }}>👤 Deelnemer</button>
          <button onClick={() => { setShowAdminLogin(true); setLocalError(''); }} style={{ flex: 1, padding: '0.5rem', borderRadius: '0.3rem', border: 'none', background: showAdminLogin ? '#dc2626' : 'transparent', color: showAdminLogin ? 'white' : '#9ca3af', fontWeight: 'bold', cursor: 'pointer', fontSize: '0.9rem' }}>🔐 Admin</button>
        </div>
        <p style={{ color: '#9ca3af', marginBottom: '2rem' }}>{showAdminLogin ? 'Admin inloggen' : (mode === 'login' ? 'Log in om mee te doen' : 'Maak een account aan')}</p>
        {(error || localError) && <div style={{ background: '#dc2626', color: 'white', padding: '0.75rem', borderRadius: '0.5rem', marginBottom: '1rem', fontSize: '0.9rem' }}>{localError || error}</div>}
        {successMessage && <div style={{ background: '#059669', color: 'white', padding: '0.75rem', borderRadius: '0.5rem', marginBottom: '1rem', fontSize: '0.9rem' }}>{successMessage}</div>}
        {showAdminLogin ? (
          <form onSubmit={handleAdminLogin}>
            <input type="text" placeholder="Admin naam" value={name} onChange={e => setName(e.target.value)} style={{ width: '100%', padding: '0.75rem', fontSize: '1rem', borderRadius: '0.5rem', background: '#111', color: 'white', border: '1px solid #dc2626', boxSizing: 'border-box', marginBottom: '1rem' }} required />
            <input type="password" placeholder="Wachtwoord" value={password} onChange={e => setPassword(e.target.value)} style={{ width: '100%', padding: '0.75rem', fontSize: '1rem', borderRadius: '0.5rem', background: '#111', color: 'white', border: '1px solid #dc2626', boxSizing: 'border-box', marginBottom: '1.5rem' }} required />
            <button type="submit" style={{ width: '100%', padding: '0.75rem', fontSize: '1rem', borderRadius: '0.5rem', background: '#dc2626', color: 'white', fontWeight: 'bold', border: 'none', cursor: 'pointer' }}>🔐 Admin Inloggen</button>
          </form>
        ) : (
          <form onSubmit={handleSubmit}>
            <input type="text" placeholder="Naam" value={name} onChange={e => setName(e.target.value)} style={{ width: '100%', padding: '0.75rem', fontSize: '1rem', borderRadius: '0.5rem', background: '#111', color: 'white', border: '1px solid #fbbf24', boxSizing: 'border-box', marginBottom: '1rem' }} required />
            <input type="password" placeholder="Wachtwoord" value={password} onChange={e => setPassword(e.target.value)} style={{ width: '100%', padding: '0.75rem', fontSize: '1rem', borderRadius: '0.5rem', background: '#111', color: 'white', border: '1px solid #fbbf24', boxSizing: 'border-box', marginBottom: mode === 'register' ? '1rem' : '1.5rem' }} required />
            {mode === 'register' && (
              <input type="password" placeholder="Bevestig wachtwoord" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} style={{ width: '100%', padding: '0.75rem', fontSize: '1rem', borderRadius: '0.5rem', background: '#111', color: 'white', border: '1px solid #fbbf24', boxSizing: 'border-box', marginBottom: '1.5rem' }} required />
            )}
            <button type="submit" style={{ width: '100%', padding: '0.75rem', fontSize: '1rem', borderRadius: '0.5rem', background: '#fbbf24', color: '#1f2937', fontWeight: 'bold', border: 'none', cursor: 'pointer', marginBottom: '1rem' }}>
              {mode === 'login' ? '🔐 Inloggen' : '✅ Registreren'}
            </button>
          </form>
        )}
        {!showAdminLogin && (
          <button onClick={() => { setMode(mode === 'login' ? 'register' : 'login'); setLocalError(''); }} style={{ background: 'transparent', color: '#fbbf24', border: 'none', cursor: 'pointer', textDecoration: 'underline', fontSize: '0.9rem' }}>
            {mode === 'login' ? 'Nog geen account? Registreer hier' : 'Al een account? Log hier in'}
          </button>
        )}
        <div style={{ marginTop: '2rem', paddingTop: '1rem', borderTop: '1px solid #374151' }}>
          <p style={{ color: '#9ca3af', fontSize: '0.8rem', marginBottom: '0.5rem' }}>Wachtwoord vergeten?</p>
          <p style={{ color: '#6b7280', fontSize: '0.75rem' }}>Neem contact op met de admin via WhatsApp voor een reset</p>
        </div>
      </div>
    </div>
  );
}

// ─── HOOFD APP ────────────────────────────────────────────────────────────────
export default function App() {
  const [currentUser, setCurrentUser] = useState(null);
  const [authError, setAuthError] = useState('');
  const [authSuccess, setAuthSuccess] = useState('');
  const [needsSetup, setNeedsSetup] = useState(false);
  const [isCheckingSetup, setIsCheckingSetup] = useState(true);

  const [name, setName] = useState("");
  const [predictions, dispatchPredictions] = useReducer(predictionsReducer, {});
  const [results, setResults] = useState({});

  const [isAdmin, setIsAdmin] = useState(false);
  const [selectedRace, setSelectedRace] = useState("Selecteer race");
  const [selectedPlayer, setSelectedPlayer] = useState("");
  const [showInfo, setShowInfo] = useState(false);
  const [showAlreadySubmitted, setShowAlreadySubmitted] = useState(false);
  const dismissedCombos = useRef(new Set());
  const submittedCombosThisSession = useRef(new Set());
  const activeEditingCombo = useRef(null);

  const [allUsers, setAllUsers] = useState([]);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [newUserName, setNewUserName] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [newUserIsAdmin, setNewUserIsAdmin] = useState(false);
  const [adminEditingPlayer, setAdminEditingPlayer] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState(null);

  // ─── SETUP CHECK ────────────────────────────────────────────────────────────
  useEffect(() => {
    const checkSetup = async () => {
      try {
        const usersData = await sbGetUsers();
        setNeedsSetup(!usersData || Object.keys(usersData).length === 0);
      } catch (error) {
        console.error('Setup check error:', error);
        setNeedsSetup(false);
      } finally {
        setIsCheckingSetup(false);
      }
    };
    checkSetup();
  }, []);

  // ─── HERSTEL SESSIE UIT LOCALSTORAGE ───────────────────────────────────────
  useEffect(() => {
    const savedUser = localStorage.getItem('f1pool_user');
    if (savedUser) {
      try {
        const userData = JSON.parse(savedUser);
        setCurrentUser(userData);
        setName(userData.name);
        setIsAdmin(userData.isAdmin || false);
      } catch (e) {
        localStorage.removeItem('f1pool_user');
      }
    }
  }, []);

  // ─── ADMIN: LAAD ALLE USERS (vervanger van onSnapshot) ─────────────────────
  // Supabase heeft ook realtime subscriptions, maar hier gebruiken we hetzelfde
  // patroon als de originele app: laden bij inloggen als admin.
  useEffect(() => {
    if (!isAdmin) return;
    const loadUsers = async () => {
      try {
        const { data, error } = await supabase.from("users").select("*");
        if (error) throw error;
        cachedUsersData = {};
        const usersList = (data || []).map(u => {
          cachedUsersData[u.name] = { password: u.password, isAdmin: u.is_admin, createdAt: u.created_at };
          return { name: u.name, isAdmin: u.is_admin, createdAt: u.created_at };
        });
        usersList.sort((a, b) => a.name.localeCompare(b.name));
        setAllUsers(usersList);
      } catch (err) {
        console.error('Fout bij laden users:', err);
      }
    };
    loadUsers();
  }, [isAdmin]);

  // ─── DATA LADEN ─────────────────────────────────────────────────────────────
  const loadData = useCallback(async () => {
    setIsRefreshing(true);
    try {
      // Ververs gebruikerslijst als admin (cache wissen zodat verse data opgehaald wordt)
      if (isAdmin) {
        cachedUsersData = null;
        const { data } = await supabase.from("users").select("*");
        if (data) {
          cachedUsersData = {};
          const usersList = data.map(u => {
            cachedUsersData[u.name] = { password: u.password, isAdmin: u.is_admin, createdAt: u.created_at };
            return { name: u.name, isAdmin: u.is_admin, createdAt: u.created_at };
          });
          usersList.sort((a, b) => a.name.localeCompare(b.name));
          setAllUsers(usersList);
        }
      }
      const d = await sbGetRoomData();
      setResults(d.results || {});
      const newPredictions = d.predictions || {};
      dispatchPredictions({ type: 'sync', payload: newPredictions });
      setLastRefreshed(new Date());
    } catch (e) {
      console.error('Laad fout:', e);
    } finally {
      setIsRefreshing(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    if (currentUser) loadData();
  }, [currentUser]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── AUTH HANDLERS ───────────────────────────────────────────────────────────
  const handleSetupComplete = async (setupName) => {
    setAuthError('');
    try {
      const user = { name: setupName, isAdmin: true };
      setCurrentUser(user);
      setName(setupName);
      setIsAdmin(true);
      setNeedsSetup(false);
      localStorage.setItem('f1pool_user', JSON.stringify(user));
    } catch (error) {
      setAuthError('Er ging iets mis bij het inloggen na setup');
    }
  };

  const handleLogin = async (loginName, password, isAdminAttempt = false) => {
    setAuthError('');
    setAuthSuccess('');
    if (!loginName || !password) { setAuthError('Vul naam en wachtwoord in'); return; }
    try {
      const usersData = await sbGetUsers();
      const userData = usersData[loginName];
      if (!userData) { setAuthError(isAdminAttempt ? 'Admin niet gevonden' : 'Gebruiker niet gevonden'); return; }
      if (isAdminAttempt && !userData.isAdmin) { setAuthError('Deze gebruiker heeft geen admin rechten'); return; }
      const passwordMatch = userData.password.startsWith('$2')
        ? await bcrypt.compare(password, userData.password)
        : userData.password === password; // fallback voor bestaande plaintext wachtwoorden
      if (!passwordMatch) { setAuthError('Ongeldig wachtwoord'); return; }
      const user = { name: loginName, isAdmin: userData.isAdmin || false };
      setCurrentUser(user);
      setName(loginName);
      setIsAdmin(userData.isAdmin || false);
      localStorage.setItem('f1pool_user', JSON.stringify(user));
    } catch (error) {
      console.error('Login error:', error);
      setAuthError('Er ging iets mis. Probeer opnieuw.');
    }
  };

  const handleRegister = async (registerName, password) => {
    setAuthError('');
    setAuthSuccess('');
    if (!registerName || !password) { setAuthError('Vul naam en wachtwoord in'); return; }
    try {
      const usersData = await sbGetUsers();
      if (usersData[registerName]) { setAuthError('Deze naam is al in gebruik'); return; }
      await sbCreateUser(registerName, password, false);
      setAuthSuccess('Account aangemaakt! Je kunt nu inloggen.');
    } catch (error) {
      console.error('Register error:', error);
      setAuthError('Er ging iets mis. Probeer opnieuw.');
    }
  };

  const handleLogout = () => {
    setCurrentUser(null);
    setName('');
    setIsAdmin(false);
    localStorage.removeItem('f1pool_user');
    setAdminEditingPlayer(null);
  };

  // ─── IDLE TIMER ──────────────────────────────────────────────────────────────
  const idleTimerRef = useRef(null);
  const [idleWarning, setIdleWarning] = useState(false);

  useEffect(() => {
    if (!currentUser) return;
    const IDLE_TIMEOUT = 3 * 60 * 1000;
    const WARNING_AT = 2.5 * 60 * 1000;

    const resetTimer = () => {
      setIdleWarning(false);
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => {
        setIdleWarning(true);
        idleTimerRef.current = setTimeout(() => {
          setIdleWarning(false);
          handleLogout();
        }, IDLE_TIMEOUT - WARNING_AT);
      }, WARNING_AT);
    };

    const events = ['mousemove', 'keydown', 'mousedown', 'touchstart', 'scroll', 'click'];
    events.forEach(e => window.addEventListener(e, resetTimer));
    resetTimer();
    return () => {
      clearTimeout(idleTimerRef.current);
      events.forEach(e => window.removeEventListener(e, resetTimer));
    };
  }, [currentUser]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── ADMIN ACTIES ────────────────────────────────────────────────────────────
  const startAdminEditing = (playerName) => {
    if (!playerName) { alert("Selecteer eerst een deelnemer"); return; }
    setAdminEditingPlayer(playerName);
    setName(playerName);
    alert(`Je bewerkt nu de voorspellingen van ${playerName}. Als admin kun je ALLES wijzigen, ook van afgelopen races!`);
  };

  const stopAdminEditing = () => {
    setAdminEditingPlayer(null);
    setName(currentUser.name);
  };

  const createNewUser = async () => {
    if (!newUserName.trim() || !newUserPassword) { alert('Vul naam en wachtwoord in'); return; }
    if (newUserPassword.length < 4) { alert('Wachtwoord moet minimaal 4 tekens bevatten'); return; }
    try {
      const usersData = cachedUsersData || {};
      if (usersData[newUserName.trim()]) { alert('Deze naam is al in gebruik'); return; }
      await sbCreateUser(newUserName.trim(), newUserPassword, newUserIsAdmin);
      // Ververs de userlijst
      const { data } = await supabase.from("users").select("*");
      const usersList = (data || []).map(u => ({ name: u.name, isAdmin: u.is_admin }));
      usersList.sort((a, b) => a.name.localeCompare(b.name));
      setAllUsers(usersList);
      alert(`Gebruiker ${newUserName.trim()} is aangemaakt! ${newUserIsAdmin ? '(Admin rechten)' : ''}`);
      setNewUserName('');
      setNewUserPassword('');
      setNewUserIsAdmin(false);
      setShowCreateUser(false);
    } catch (error) {
      alert('Er ging iets mis bij het aanmaken van de gebruiker');
    }
  };

  const toggleAdminStatus = async (userName, currentStatus) => {
    if (userName === currentUser?.name) { alert('Je kunt je eigen admin rechten niet verwijderen!'); return; }
    if (!window.confirm(`Wil je ${userName} ${currentStatus ? 'geen admin' : 'admin'} maken?`)) return;
    try {
      await sbToggleAdmin(userName, !currentStatus);
      setAllUsers(prev => prev.map(u => u.name === userName ? { ...u, isAdmin: !currentStatus } : u));
      alert(`${userName} is nu ${!currentStatus ? 'admin' : 'geen admin meer'}!`);
    } catch (error) {
      alert('Er ging iets mis bij het wijzigen van admin rechten');
    }
  };

  const deleteUser = async (userName) => {
    if (userName === currentUser?.name) { alert('Je kunt je eigen account niet verwijderen terwijl je ingelogd bent!'); return; }
    if (userName === adminEditingPlayer) { alert('Je kunt de gebruiker die je aan het bewerken bent niet verwijderen!'); return; }
    if (!window.confirm(`⚠️ WAARSCHUWING!\n\nJe staat op het punt om ${userName} VOLLEDIG te verwijderen.\n\nDit verwijdert:\n- Het gebruikersaccount (login gegevens)\n- ALLE voorspellingen van deze gebruiker\n- De gebruiker uit het klassement\n\nDeze actie kan NIET ongedaan worden gemaakt!\n\nWil je doorgaan?`)) return;
    try {
      await sbDeletePlayerPredictions(userName);
      await sbDeleteUser(userName);
      setAllUsers(prev => prev.filter(u => u.name !== userName));
      dispatchPredictions({ type: 'deletePlayer', payload: userName });
      if (selectedPlayer === userName) setSelectedPlayer("");
      alert(`✅ ${userName} is volledig verwijderd!`);
    } catch (error) {
      console.error('Delete user error:', error);
      alert(`❌ Er ging iets mis bij het verwijderen van ${userName}.\n\nFout: ${error.message}`);
    }
  };

  const resetUserPassword = async () => {
    if (!isAdmin || !selectedPlayer) { alert("Selecteer eerst een deelnemer om het wachtwoord te resetten"); return; }
    const newPassword = prompt(`Nieuw wachtwoord voor ${selectedPlayer}:`);
    if (!newPassword || newPassword.length < 4) { alert('Wachtwoord moet minimaal 4 tekens bevatten'); return; }
    try {
      await sbUpdateUserPassword(selectedPlayer, newPassword);
      alert(`Wachtwoord van ${selectedPlayer} is gereset!`);
    } catch (error) {
      alert('Er ging iets mis bij het resetten van het wachtwoord');
      console.error(error);
    }
  };

  // ─── RACE LOGICA ─────────────────────────────────────────────────────────────
  const isRaceLocked = useCallback((raceName) => {
    const race = raceSchedule.find(r => r.name === raceName);
    if (!race || !race.start) return false;
    return new Date() >= new Date(race.start);
  }, []);

  const getLockedRaces = useCallback(() => {
    return raceSchedule.slice(1).filter(r => isRaceLocked(r.name));
  }, [isRaceLocked]);

  const hasExistingPredictionsForRace = useCallback((playerName, raceName) => {
    if (!playerName?.trim() || !raceName || raceName === "Selecteer race") return false;
    const pp = predictions[playerName.trim()]?.[raceName];
    return pp && Object.keys(pp).length > 0;
  }, [predictions]);

  const hasSubmittedThisSession = useCallback((playerName, raceName) => {
    if (!playerName?.trim() || !raceName) return false;
    return submittedCombosThisSession.current.has(`${playerName.trim()}-${raceName}`);
  }, []);

  const isEditingThisCombo = useCallback((playerName, raceName) => {
    if (!playerName?.trim() || !raceName) return false;
    return activeEditingCombo.current === `${playerName.trim()}-${raceName}`;
  }, []);

  const isDismissed = useCallback((playerName, raceName) => {
    if (!playerName?.trim() || !raceName) return false;
    return dismissedCombos.current.has(`${playerName.trim()}-${raceName}`);
  }, []);

  const shouldShowPredictionFields = useCallback((raceName, playerName = name) => {
    if (isAdmin && adminEditingPlayer && playerName === adminEditingPlayer) return true;
    if (isAdmin && !adminEditingPlayer && playerName === currentUser?.name) {
      if (isRaceLocked(raceName)) return false;
      return true;
    }
    if (isRaceLocked(raceName)) return false;
    return true;
  }, [isAdmin, adminEditingPlayer, currentUser, isRaceLocked, name]);

  const shouldShowAlreadySubmittedMessage = useCallback((raceName, playerName = name) => {
    if (isAdmin && adminEditingPlayer && playerName === adminEditingPlayer) return false;
    if (!isRaceLocked(raceName)) return false;
    const cleanName = playerName?.trim();
    if (!cleanName || !raceName || raceName === "Selecteer race") return false;
    return hasExistingPredictionsForRace(cleanName, raceName);
  }, [isAdmin, adminEditingPlayer, isRaceLocked, hasExistingPredictionsForRace, name]);

  const predict = async (key, value) => {
    const targetPlayer = adminEditingPlayer || name.trim();
    if (!targetPlayer || !selectedRace || selectedRace === "Selecteer race") return;
    activeEditingCombo.current = `${targetPlayer}-${selectedRace}`;
    dispatchPredictions({ type: 'update', payload: { player: targetPlayer, race: selectedRace, key, value } });
    await sbUpdatePrediction(targetPlayer, selectedRace, key, value);
    const currentPredictions = (predictions[targetPlayer]?.[selectedRace]) || {};
    const updatedPredictions = { ...currentPredictions, [key]: value };
    const allFieldsFilled = predictionOrder.every(field => updatedPredictions[field]);
    if (allFieldsFilled) {
      submittedCombosThisSession.current.add(`${targetPlayer}-${selectedRace}`);
      activeEditingCombo.current = null;
      setShowAlreadySubmitted(false);
    }
  };

  const updateResult = async (key, value) => {
    if (!isAdmin) return;
    const updated = { ...results, [selectedRace]: { ...(results[selectedRace] || {}), [key]: value } };
    setResults(updated);
    const current = await sbGetRoomData();
    await sbUpsertRoom(current.predictions || {}, updated);
  };

  const clearRace = async () => {
    if (!isAdmin || selectedRace === "Selecteer race") return;
    if (!window.confirm(`Reset ${selectedRace}?`)) return;
    const { results: newResults } = await sbClearRace(selectedRace, predictions);
    dispatchPredictions({ type: 'clearRace', payload: selectedRace });
    setResults(newResults);
  };

  const deletePlayerRacePredictions = async () => {
    if (!isAdmin || !selectedPlayer || selectedRace === "Selecteer race") { alert("Selecteer eerst een deelnemer en een race"); return; }
    if (!window.confirm(`Verwijder alle voorspellingen van ${selectedPlayer} voor ${selectedRace}?`)) return;
    dispatchPredictions({ type: 'deletePlayerRace', payload: { player: selectedPlayer, race: selectedRace } });
    await sbDeletePlayerRacePredictions(selectedPlayer, selectedRace);
    submittedCombosThisSession.current.delete(`${selectedPlayer}-${selectedRace}`);
    dismissedCombos.current.delete(`${selectedPlayer}-${selectedRace}`);
    alert(`Voorspellingen van ${selectedPlayer} voor ${selectedRace} zijn verwijderd!`);
  };


  const handleCloseAlreadySubmitted = useCallback(() => {
    const cleanName = name.trim();
    if (cleanName && selectedRace && selectedRace !== "Selecteer race") {
      dismissedCombos.current.add(`${cleanName}-${selectedRace}`);
    }
    setShowAlreadySubmitted(false);
  }, [name, selectedRace]);

  // ─── AFGELEIDE STATE ─────────────────────────────────────────────────────────
  const predictionOrder = ["pole", "first", "second", "third", "latest"];

  const activePlayers = useMemo(() => {
    return Object.keys(predictions)
      .filter(player => {
        const playerData = predictions[player];
        return playerData && Object.values(playerData).some(r => r && Object.keys(r).length > 0);
      })
      .sort((a, b) => a.localeCompare(b));
  }, [predictions]);

  const leaderboard = useMemo(() => {
    return activePlayers
      .map(p => ({ name: p, score: calculateScore(predictions[p], results) }))
      .sort((a, b) => {
        if (b.score !== a.score) return b.score - a.score;
        return a.name.localeCompare(b.name);
      });
  }, [activePlayers, predictions, results]);

  const lockedRaces = useMemo(() => getLockedRaces(), [getLockedRaces]);

  const overviewData = useMemo(() => {
    return activePlayers
      .filter(player => {
        const user = allUsers.find(u => u.name === player);
        const isAdminUser = user && user.isAdmin;
        const hasPredictions = predictions[player] && Object.keys(predictions[player]).length > 0;
        return !isAdminUser && hasPredictions;
      })
      .map(player => ({
        name: player,
        races: lockedRaces.map(r => {
          const playerPreds = predictions[player] || {};
          return playerPreds[r.name] || {};
        })
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [activePlayers, predictions, lockedRaces, allUsers]);

  const currentRaceLocked = selectedRace !== "Selecteer race" && isRaceLocked(selectedRace);

  useEffect(() => {
    if (selectedRace === "Selecteer race" || !name.trim()) {
      setShowAlreadySubmitted(false);
      activeEditingCombo.current = null;
      return;
    }
    const cleanName = name.trim();
    // Het blauwe scherm mag alleen verschijnen als de race gelocked is
    if (!isRaceLocked(selectedRace)) {
      setShowAlreadySubmitted(false);
      return;
    }
    if (!isEditingThisCombo(cleanName, selectedRace)) {
      const shouldShow = hasExistingPredictionsForRace(cleanName, selectedRace) &&
        !hasSubmittedThisSession(cleanName, selectedRace) &&
        !isDismissed(cleanName, selectedRace);
      setShowAlreadySubmitted(shouldShow);
    }
  }, [selectedRace, name, hasExistingPredictionsForRace, hasSubmittedThisSession, isEditingThisCombo, isDismissed, isRaceLocked]);

  const showFields = shouldShowPredictionFields(selectedRace, name);
  const showAlreadySubmittedMessage = showAlreadySubmitted || shouldShowAlreadySubmittedMessage(selectedRace, name);

  const adminUsers = useMemo(() => allUsers.filter(u => u.isAdmin).sort((a, b) => a.name.localeCompare(b.name)), [allUsers]);
  const regularUsers = useMemo(() => allUsers.filter(u => !u.isAdmin).sort((a, b) => a.name.localeCompare(b.name)), [allUsers]);

  // ─── RENDER ───────────────────────────────────────────────────────────────────
  if (isCheckingSetup) {
    return (
      <div style={{ minHeight: '100vh', background: 'linear-gradient(to bottom right,#b91c1c,#000,#1f2937)', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <div style={{ color: '#fbbf24', fontSize: '1.5rem' }}>⏳ Laden...</div>
      </div>
    );
  }

  if (needsSetup) return <SetupScreen onSetupComplete={handleSetupComplete} />;

  if (!currentUser) {
    return <AuthScreen onLogin={handleLogin} onRegister={handleRegister} error={authError} successMessage={authSuccess} />;
  }

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(to bottom right,#b91c1c,#000,#1f2937)', padding: '1rem' }}>
      {idleWarning && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999, background: '#dc2626', color: 'white', padding: '0.75rem', textAlign: 'center', fontWeight: 'bold' }}>
          ⚠️ Je wordt over 30 seconden uitgelogd wegens inactiviteit. Beweeg je muis of druk een toets in om te blijven.
        </div>
      )}
      <div style={{ maxWidth: '480px', margin: '0 auto' }}>
        <img src="/f1-header.jpg" alt="F1 Header" style={{ width: '100%', borderRadius: '0.75rem', marginBottom: '1rem', display: 'block' }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h1 style={{ color: '#ef4444', margin: 0, fontSize: '1.5rem' }}>🏁 F1 Pool 2026</h1>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <button onClick={() => setShowInfo(!showInfo)} style={{ padding: '0.3rem 0.6rem', borderRadius: '0.4rem', background: '#374151', color: '#fbbf24', border: 'none', cursor: 'pointer', fontSize: '0.8rem' }}>ℹ️ Info</button>
            <button onClick={handleLogout} style={{ padding: '0.3rem 0.6rem', borderRadius: '0.4rem', background: '#374151', color: 'white', border: 'none', cursor: 'pointer', fontSize: '0.8rem' }}>🚪 Uit</button>
          </div>
        </div>

        {showInfo && (
          <div style={{ background: '#1f2937', border: '1px solid #fbbf24', borderRadius: '0.5rem', padding: '1rem', marginBottom: '1rem', fontSize: '0.85rem', color: 'white' }}>
            <h3 style={{ color: '#fbbf24', margin: '0 0 0.5rem 0' }}>🎯 Puntensysteem</h3>
            <div>🥇 1e plaats: {points.first}pt &nbsp; 🥈 2e: {points.second}pt &nbsp; 🥉 3e: {points.third}pt</div>
            <div>🏎️ Laatste: {points.latest}pt &nbsp; 🏁 Pole: {points.pole}pt</div>
            <button onClick={() => setShowInfo(false)} style={{ marginTop: '0.5rem', padding: '0.3rem 0.8rem', borderRadius: '0.3rem', background: '#fbbf24', color: '#1f2937', border: 'none', cursor: 'pointer', fontSize: '0.8rem' }}>Sluiten</button>
          </div>
        )}

        {isAdmin && (
          <div style={{ background: '#1f2937', border: '2px solid #dc2626', borderRadius: '0.5rem', padding: '1rem', marginBottom: '1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ color: '#dc2626', margin: 0 }}>🔐 Admin Panel</h3>
              <button onClick={loadData} disabled={isRefreshing} style={{ padding: '0.3rem 0.8rem', borderRadius: '0.4rem', background: isRefreshing ? '#6b7280' : '#fbbf24', color: '#1f2937', fontWeight: 'bold', border: 'none', cursor: isRefreshing ? 'not-allowed' : 'pointer', fontSize: '0.8rem' }}>
                {isRefreshing ? '⏳ Laden...' : '🔄 Vernieuwen'}
              </button>
            </div>

            {adminEditingPlayer && (
              <div style={{ background: '#dc2626', borderRadius: '0.5rem', padding: '0.5rem', marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ color: 'white', fontSize: '0.9rem' }}>✏️ Bewerkt: <strong>{adminEditingPlayer}</strong></span>
                <button onClick={stopAdminEditing} style={{ padding: '0.2rem 0.5rem', borderRadius: '0.3rem', background: 'white', color: '#dc2626', border: 'none', cursor: 'pointer', fontSize: '0.8rem' }}>✅ Stop</button>
              </div>
            )}

            <h4 style={{ color: '#fbbf24', marginBottom: '0.5rem' }}>👥 Gebruikersbeheer</h4>
            <p style={{ color: '#9ca3af', fontSize: '0.75rem', margin: '0 0 0.75rem 0' }}>🔐 {adminUsers.length} admins · 👤 {new Set([...regularUsers.map(u => u.name), ...activePlayers]).size} deelnemers</p>

            <StyledSelect label="Selecteer deelnemer" value={selectedPlayer} options={["", ...[...new Set([...allUsers.map(u => u.name), ...activePlayers])].sort((a, b) => a.localeCompare(b))]} onChange={setSelectedPlayer} />
            {selectedPlayer && (() => {
              const selUser = allUsers.find(u => u.name === selectedPlayer);
              return (
                <div style={{ background: '#111', borderRadius: '0.5rem', padding: '0.75rem', marginBottom: '1rem', border: '1px solid #374151' }}>
                  <p style={{ color: selUser?.isAdmin ? '#fbbf24' : '#9ca3af', fontSize: '0.75rem', margin: '0 0 0.6rem 0', fontWeight: 'bold' }}>
                    {selUser?.isAdmin ? '🔐 Admin' : '👤 Deelnemer'} — {selectedPlayer}
                  </p>
                  <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                    <button onClick={() => startAdminEditing(selectedPlayer)} style={{ padding: '0.3rem 0.6rem', fontSize: '0.8rem', borderRadius: '0.3rem', background: '#fbbf24', color: '#1f2937', border: 'none', cursor: 'pointer' }}>✏️ Bewerk</button>
                    <button onClick={() => toggleAdminStatus(selectedPlayer, selUser?.isAdmin)} style={{ padding: '0.3rem 0.6rem', fontSize: '0.8rem', borderRadius: '0.3rem', background: '#374151', color: 'white', border: '1px solid #6b7280', cursor: 'pointer' }}>🔐 {selUser?.isAdmin ? 'Geen admin' : 'Maak admin'}</button>
                    <button onClick={resetUserPassword} style={{ padding: '0.3rem 0.6rem', fontSize: '0.8rem', borderRadius: '0.3rem', background: '#6366f1', color: 'white', border: 'none', cursor: 'pointer' }}>🔑 Reset ww</button>
                    <button onClick={() => deleteUser(selectedPlayer)} style={{ padding: '0.3rem 0.6rem', fontSize: '0.8rem', borderRadius: '0.3rem', background: '#dc2626', color: 'white', border: 'none', cursor: 'pointer' }}>🗑 Verwijder</button>
                  </div>
                </div>
              );
            })()}

            <button onClick={() => setShowCreateUser(!showCreateUser)} style={{ background: '#059669', color: 'white', padding: '0.4rem 0.8rem', borderRadius: '0.4rem', marginBottom: '0.5rem', border: 'none', cursor: 'pointer', fontSize: '0.85rem' }}>➕ Nieuwe gebruiker</button>
            {showCreateUser && (
              <div style={{ background: '#111', padding: '0.75rem', borderRadius: '0.5rem', marginBottom: '1rem' }}>
                <input type="text" placeholder="Naam" value={newUserName} onChange={e => setNewUserName(e.target.value)} style={{ width: '100%', padding: '0.5rem', marginBottom: '0.5rem', background: '#1f2937', color: 'white', border: '1px solid #fbbf24', borderRadius: '0.3rem', boxSizing: 'border-box' }} />
                <input type="password" placeholder="Wachtwoord" value={newUserPassword} onChange={e => setNewUserPassword(e.target.value)} style={{ width: '100%', padding: '0.5rem', marginBottom: '0.5rem', background: '#1f2937', color: 'white', border: '1px solid #fbbf24', borderRadius: '0.3rem', boxSizing: 'border-box' }} />
                <label style={{ color: 'white', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <input type="checkbox" checked={newUserIsAdmin} onChange={e => setNewUserIsAdmin(e.target.checked)} />
                  Admin rechten
                </label>
                <button onClick={createNewUser} style={{ background: '#fbbf24', color: '#1f2937', padding: '0.4rem 0.8rem', borderRadius: '0.3rem', border: 'none', cursor: 'pointer', fontWeight: 'bold' }}>✅ Aanmaken</button>
              </div>
            )}

            <hr style={{ border: '1px solid #374151', margin: '1rem 0' }} />
            <h4 style={{ color: '#fbbf24', marginBottom: '1rem' }}>🏁 Race Beheer</h4>
            <StyledSelect label="Race (voor resultaten)" value={selectedRace} options={["Selecteer race", ...raceSchedule.slice(1).map(r => r.name)]} onChange={setSelectedRace} />
            <button onClick={clearRace} style={{ background: '#dc2626', color: 'white', padding: '0.5rem', borderRadius: '0.5rem', marginBottom: '1rem', width: '100%', border: 'none', cursor: 'pointer' }}>🗑 Reset ALLE voorspellingen voor huidige race</button>
            <button onClick={deletePlayerRacePredictions} disabled={!selectedPlayer || selectedRace === "Selecteer race"} style={{ background: selectedPlayer && selectedRace !== "Selecteer race" ? '#dc2626' : '#6b7280', color: 'white', padding: '0.5rem', borderRadius: '0.5rem', marginBottom: '1rem', width: '100%', cursor: selectedPlayer && selectedRace !== "Selecteer race" ? 'pointer' : 'not-allowed', border: 'none', opacity: selectedPlayer && selectedRace !== "Selecteer race" ? 1 : 0.6 }}>
              🗑 Verwijder voorspellingen van {selectedPlayer || "..."} voor {selectedRace === "Selecteer race" ? "..." : selectedRace}
            </button>
            <hr style={{ border: '1px solid #374151', margin: '1rem 0' }} />
            <h4 style={{ color: '#fbbf24', marginBottom: '0.5rem' }}>🏁 Resultaten invoeren</h4>
            {predictionOrder.map(k => (
              <StyledSelect key={k} label={`Result ${k}`} value={results[selectedRace]?.[k] || ""} options={drivers} onChange={v => updateResult(k, v)} />
            ))}
          </div>
        )}

        <StyledSelect label="Race" value={selectedRace} options={["Selecteer race", ...raceSchedule.slice(1).map(r => r.name)]} onChange={setSelectedRace} />

        {currentRaceLocked && !isAdmin && <LockMessage raceName={selectedRace} />}

        {currentRaceLocked && isAdmin && adminEditingPlayer && (
          <div style={{ background: 'linear-gradient(135deg,#dc2626 0%,#991b1b 100%)', border: '2px solid #fbbf24', borderRadius: '1rem', padding: '1rem', marginBottom: '1rem', textAlign: 'center' }}>
            <div style={{ fontSize: '1.5rem', marginBottom: '0.3rem' }}>🔓</div>
            <h3 style={{ color: '#fbbf24', margin: '0 0 0.3rem 0', fontSize: '1rem' }}>Admin Override Actief</h3>
            <p style={{ color: 'white', margin: 0, fontSize: '0.85rem' }}><strong>{selectedRace}</strong> is gelocked, maar jij kunt als admin alles wijzigen voor <strong>{adminEditingPlayer}</strong>.</p>
          </div>
        )}

        {showAlreadySubmittedMessage && !isAdmin && <AlreadySubmittedMessage raceName={selectedRace} onClose={handleCloseAlreadySubmitted} />}
        {showAlreadySubmittedMessage && isAdmin && !adminEditingPlayer && <AlreadySubmittedMessage raceName={selectedRace} onClose={handleCloseAlreadySubmitted} />}

        {showFields && (
          <div style={{ background: '#111', padding: '0.75rem', borderRadius: '0.5rem', marginBottom: '1rem', border: adminEditingPlayer ? '2px solid #dc2626' : '1px solid #fbbf24' }}>
            <p style={{ color: '#fbbf24', margin: '0 0 0.5rem 0', fontSize: '0.9rem' }}>
              {adminEditingPlayer ? (
                <>✏️ Je bewerkt: <strong style={{ color: '#dc2626' }}>{adminEditingPlayer}</strong>{currentRaceLocked && <span style={{ color: '#dc2626', display: 'block', marginTop: '0.3rem' }}>🔓 Admin override actief - je kunt alles wijzigen!</span>}</>
              ) : (
                <>👤 Ingelogd als: <strong>{currentUser.name}</strong></>
              )}
            </p>
          </div>
        )}

        {showFields && predictionOrder.map((k) => {
          const playerPreds = predictions[name] || {};
          const racePreds = playerPreds[selectedRace] || {};
          return <StyledSelect key={k} label={k} value={racePreds[k] || ""} options={drivers} onChange={v => predict(k, v)} />;
        })}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1rem', marginBottom: '0.25rem' }}>
          <h2 style={{ color: '#ef4444', margin: 0 }}>🏆 Klassement</h2>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.2rem' }}>
            <button onClick={loadData} disabled={isRefreshing} style={{ padding: '0.3rem 0.8rem', borderRadius: '0.4rem', background: isRefreshing ? '#6b7280' : '#fbbf24', color: '#1f2937', fontWeight: 'bold', border: 'none', cursor: isRefreshing ? 'not-allowed' : 'pointer', fontSize: '0.85rem' }}>
              {isRefreshing ? '⏳ Laden...' : '🔄 Vernieuwen'}
            </button>
            {lastRefreshed && (
              <span style={{ color: '#6b7280', fontSize: '0.7rem' }}>
                Bijgewerkt: {lastRefreshed.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
          </div>
        </div>
        {leaderboard.map((p, i) => (
          <div key={p.name} style={{ display: 'flex', justifyContent: 'space-between', color: '#fbbf24' }}>
            <span>{i + 1}. {p.name}</span>
            <strong>{p.score}</strong>
          </div>
        ))}

        <h2 style={{ color: '#facc15', marginTop: '1rem' }}>📊 Overzicht voorspellingen</h2>

        {lockedRaces.length === 0 && (
          <div style={{ background: '#374151', border: '2px solid #fbbf24', borderRadius: '1rem', padding: '1.5rem', marginBottom: '1rem', textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>⏳</div>
            <h3 style={{ color: '#fbbf24', margin: '0 0 0.5rem 0', fontSize: '1.1rem' }}>Nog geen races gestart</h3>
            <p style={{ color: 'white', margin: 0, fontSize: '0.9rem' }}>Het overzicht wordt zichtbaar zodra de eerste race is gelocked (qualifying is begonnen).</p>
          </div>
        )}

        {lockedRaces.length > 0 && (
          <div style={{ overflowX: 'auto', maxWidth: '100%', border: '2px solid #fbbf24', borderRadius: '0.5rem', background: '#111' }}>
            <table style={{ borderCollapse: 'separate', borderSpacing: 0, width: 'max-content', minWidth: '100%' }}>
              <thead>
                <tr>
                  <th style={{ border: '1px solid #fbbf24', borderRight: '2px solid #fbbf24', padding: '0.3rem', color: '#fbbf24', background: '#1f2937', position: 'sticky', left: 0, zIndex: 10, minWidth: '80px', maxWidth: '100px', boxShadow: '2px 0 5px rgba(0,0,0,0.3)', fontSize: '0.7rem' }}>Speler</th>
                  {lockedRaces.map(r => (
                    <th key={r.name} style={{ border: '1px solid #fbbf24', padding: '0.4rem', color: '#fbbf24', background: '#1f2937', minWidth: '120px', fontSize: '0.7rem', whiteSpace: 'nowrap' }}>{r.name}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {overviewData.map((player, playerIndex) => (
                  <tr key={player.name} style={{ background: playerIndex % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.05)' }}>
                    <td style={{ border: '1px solid #fbbf24', borderRight: '2px solid #fbbf24', padding: '0.3rem', color: '#fbbf24', background: '#1f2937', position: 'sticky', left: 0, zIndex: 5, fontWeight: 'bold', boxShadow: '2px 0 5px rgba(0,0,0,0.3)', fontSize: '0.65rem', maxWidth: '100px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={player.name}>{player.name}</td>
                    {player.races.map((racePred, raceIndex) => {
                      const raceName = lockedRaces[raceIndex].name;
                      const hasPredictions = racePred && Object.keys(racePred).length > 0;
                      const raceResults = results[raceName];
                      return (
                        <td key={raceIndex} style={{ border: '1px solid #fbbf24', padding: '0.2rem', textAlign: 'left', fontSize: '0.65rem', minWidth: '120px', background: hasPredictions ? 'rgba(251,191,36,0.1)' : 'transparent' }}>
                          {predictionOrder.map(pos => {
                            const predictedDriver = racePred[pos];
                            if (!predictedDriver) return null;
                            const isCorrect = raceResults && raceResults[pos] === predictedDriver;
                            return (
                              <div key={pos} style={{ marginBottom: '0.1rem', padding: '0.1rem', borderRadius: '0.2rem', background: isCorrect ? 'rgba(34,197,94,0.3)' : 'transparent', border: isCorrect ? '1px solid #22c55e' : 'none' }}>
                                <span style={{ color: isCorrect ? '#22c55e' : 'white', fontWeight: isCorrect ? 'bold' : 'normal', fontSize: '0.6rem' }}>{pos}:</span>
                                <span style={{ color: isCorrect ? '#22c55e' : '#fbbf24', fontWeight: isCorrect ? 'bold' : 'normal', marginLeft: '0.2rem', fontSize: '0.6rem' }}>{predictedDriver} {isCorrect && '✓'}</span>
                              </div>
                            );
                          }).filter(Boolean)}
                          {!hasPredictions && <span style={{ color: '#6b7280', fontStyle: 'italic', fontSize: '0.6rem' }}>Geen voorspelling</span>}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}